// Screen.tsx — the standard padded canvas for a tab screen, with a warm lamp
// key-light from the top. Top padding clears the status bar / notch via the
// safe-area inset; bottom padding clears the tab bar.
import React from 'react';
import { useIsDesktop } from '@/hooks/useIsDesktop';

export function Screen({
  children,
  label,
  surface = 'plain',
  center = false,
}: {
  children: React.ReactNode;
  label: string;
  /** 'deep' lays the content on a slightly darker warm canvas, so the frosted
   *  header reads as a distinct, lighter surface above the feed (used on Today). */
  surface?: 'plain' | 'deep';
  /** When true, the content fills the viewport height and is centred vertically
   *  instead of stacking at the top. Used by Today when the programme feed is
   *  hidden, so a short landing sits balanced on a tall/desktop screen rather than
   *  leaving a large void beneath it. */
  center?: boolean;
}) {
  const deep = surface === 'deep';
  const isDesktop = useIsDesktop();
  // Vertical centring is a DESKTOP-only concern: a tall window leaves a void under a
  // short landing. On a phone the content already fills the (short) viewport and the
  // layout is already right, so we leave it completely untouched.
  const doCenter = center && isDesktop;
  // On mobile the header + tab bar are sticky, in-flow elements inside the single
  // scroller (see AppShell), so they reserve their own space and carry their own
  // safe-area insets — the content only needs ordinary padding. On desktop there is no
  // in-flow header/tab bar (a SideNav is used), so the content keeps the original
  // header/tab-bar clearance it always had.
  const contentPadding = isDesktop
    ? 'calc(var(--safe-top) + var(--frame-header-h) + 16px) calc(var(--screen-px) + var(--safe-right)) calc(122px + var(--safe-bottom)) calc(var(--screen-px) + var(--safe-left))'
    : '16px calc(var(--screen-px) + var(--safe-right)) 28px calc(var(--screen-px) + var(--safe-left))';
  return (
    <div
      data-screen={label}
      style={{
        position: 'relative',
        // On desktop, fill the viewport so the centred content has height to sit in;
        // percentage min-height doesn't stretch this content-sized column, so use dvh.
        minHeight: doCenter ? '100dvh' : '100%',
        ...(doCenter ? { display: 'flex', flexDirection: 'column' as const } : {}),
        // Light-led canvas: a top-pooled light wash over a gentle top-lighter
        // gradient. Light gathers at the top (a soft lamp) and the edges are
        // NEVER darkened — cards lift off it via their own top highlight.
        background: deep
          ? 'var(--top-light), var(--canvas-grad)'
          : undefined,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 320,
          background:
            'radial-gradient(120% 76% at 50% -14%, rgba(255,250,240,0.9), rgba(246,224,190,0.16) 42%, transparent 64%)',
          // Softened on the deep canvas so the bright halo doesn't fight the
          // header/feed separation.
          opacity: deep ? 0.4 : 1,
          pointerEvents: 'none',
        }}
      />
      {/* A faint seam right under the header, so the feed reads as its own plane. */}
      {deep && (
        <div
          style={{
            position: 'absolute',
            // Mobile: content starts directly under the sticky in-flow header, so the
            // seam sits at the very top. Desktop: keep it below the header-height offset.
            top: isDesktop ? 'calc(var(--safe-top) + var(--frame-header-h))' : 0,
            left: 0,
            right: 0,
            height: 24,
            background: 'linear-gradient(180deg, rgba(74,52,24,0.06), transparent)',
            pointerEvents: 'none',
          }}
        />
      )}
      <div
        style={{
          position: 'relative',
          padding: contentPadding,
          ...(doCenter ? { flex: 1, display: 'flex', flexDirection: 'column' as const, justifyContent: 'center' } : {}),
        }}
      >
        {children}
      </div>
    </div>
  );
}
